import { Stack } from "@mui/material";
import React from "react";
import { useFormByIdQuery, GQLContentForm } from "@quales/data-access/graphql";
import { Form } from "@bpipartners/rjsf-web-ui";
import { useTranslations } from "next-intl";
import validator from "@bpipartners/rjsf-validator-ajv";
import FormIndexComponent from "./FormIndexComponent";
import { useTheme } from "@quales/utils/theme";
import { enqueueSnackbar } from "notistack";
import { debounce } from "lodash";

interface FormViewerCompositeProps {
  formID: string;
  useLiveValidate: boolean;
  inputFormData: any;
  children?: any;
  hideFormTitle?: boolean;
  onFormvalueChanged?: (formvalue: any, submitted: boolean) => void;
  onSubmitClicked?: (formData: any, submitted: boolean) => void;
}

export const FormViewerComposite = React.forwardRef(
  (props: FormViewerCompositeProps, ref) => {
    const {
      formID,
      useLiveValidate,
      inputFormData,
      onFormvalueChanged,
      children,
      onSubmitClicked,
      hideFormTitle,
    } = props;

    const { data: formData, loading: formLoading } = useFormByIdQuery({
      variables: {
        formID: formID,
      },
    });
    const t = useTranslations();
    const theme = useTheme();
    const submitFormRef = React.createRef();
    const [errorsIndexes, setErrorIndexs] = React.useState<string[]>([]);

    const [formContent, setFormContent] = React.useState<GQLContentForm>();
    const getHideMenu =
      (formContent &&
        formContent.uiSchema &&
        formContent.uiSchema["ui:options"] &&
        formContent.uiSchema["ui:options"].hideMenu) ||
      false;

    const [useLiveValidateState, setUseLiveValidateState] =
      React.useState(false);

    const onSubmit = (val: any, e: any) => {
      if (onSubmitClicked) {
        onSubmitClicked(val.formData, true);
      }
    };
    const onValueChanged = (val: any, error: any) => {
      //@ts-ignore
      setFormContent({
        ...formContent,
        defaultData: val.formData,
      });
      if (onFormvalueChanged) {
        debouncedSave(val.formData);
      }
    };
    const debouncedSave = React.useCallback(
      debounce(
        (nextValue: string) => onFormvalueChanged(nextValue, false),
        1000
      ),
      [onFormvalueChanged] // will be created only once initially
    );

    const handleOnError = (val) => {
      const destructOfArray = [...new Set(val.map((obj) => obj.schemaPath))];
      const subStringOfErrorArray = destructOfArray.map(
        //@ts-ignore
        (item) => item.split("/")[2] // schemaPath is like: "#/properties/Eindconclusie/required"
      );
      const finalStringArray = [
        ...new Set(subStringOfErrorArray.map((obj) => obj)),
      ];
      setErrorIndexs(finalStringArray);

      setTimeout(() => {
        enqueueSnackbar(t("feedback.missingValues"), {
          variant: "error",
        });
      }, 1000);
    };

    const transformErrors = (errors: any[]) =>
      errors.map((error: any) => {
        if (useLiveValidate && useLiveValidateState === false) {
          setUseLiveValidateState(true);
        }
        if (error.name === "required") {
          error.message = t("labels.requiredField");
        }
        return error;
      });

    React.useEffect(() => {
      if (!formLoading && formData && formData.FormByID) {
        if (inputFormData) {
          setFormContent({
            defaultData: inputFormData,
            formID: formID,
            formVersion: formData.FormByID.formVersion,
            formSchema: formData.FormByID.formSchema,
            uiSchema:
              formContent && formContent.uiSchema
                ? formContent?.uiSchema
                : formData.FormByID.uiSchema,
            description: formData.FormByID.description,
            name: formData.FormByID.name,
          });
        } else {
          setFormContent(formData.FormByID);
        }
      } else {
        setFormContent(undefined);
      }
    }, [inputFormData, formData, formLoading]);

    React.useImperativeHandle(ref, () => ({
      onSubmit() {
        if (onSubmitClicked) {
          //@ts-ignore
          submitFormRef.current.click();
        }
      },
    }));

    return (
      <Stack id="FormViewerComposite">
        {!formLoading &&
          formContent &&
          formContent &&
          formContent.formSchema && (
            <Stack direction={"row"} width={"100%"} maxWidth={1000}>
              {!getHideMenu && (
                <FormIndexComponent
                  errorIndexes={errorsIndexes}
                  data={formContent}
                />
              )}
              <Stack
                height={
                  theme.dimensions.baseHeight
                    ? theme.dimensions.baseHeight - 200
                    : "80%"
                }
                spacing={2}
                paddingBottom={2}
                overflow="auto"
              >
                <Form
                  schema={
                    formContent &&
                    formContent.formSchema !== undefined &&
                    formContent.formSchema &&
                    formContent.formSchema
                  }
                  uiSchema={
                    (formContent &&
                      formContent.uiSchema !== undefined &&
                      formContent.uiSchema) ||
                    {}
                  }
                  config={{
                    STORAGE_URL: process.env.STORAGE_URL || "/files",
                  }}
                  onSubmit={onSubmit}
                  readonly={false}
                  onError={handleOnError}
                  noHtml5Validate={true}
                  showErrorList={false}
                  onChange={onValueChanged}
                  liveValidate={useLiveValidateState}
                  formData={(formContent && formContent.defaultData) || {}}
                  ismobile={false}
                  transformErrors={transformErrors}
                  validator={validator}
                >
                  {children && children}
                  <button
                    //@ts-ignore
                    ref={submitFormRef}
                    type="submit"
                    style={{ display: "none" }}
                  />
                </Form>
              </Stack>
            </Stack>
          )}
      </Stack>
    );
  }
);

FormViewerComposite.displayName = "FormViewerComposite";
export default FormViewerComposite;
